import { ref, readonly } from 'vue'
import { getXCountry, getCountryFromApi, getCountryFromCdn } from '@/utils/http-headers'

const CACHE_KEY_COUNTRY = 'detected_country'

/**
 * Получает страну из кеша sessionStorage
 */
function getCachedCountry(): string | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null
  }

  return sessionStorage.getItem(CACHE_KEY_COUNTRY)
}

/**
 * Сохраняет страну в кеш sessionStorage
 */
function saveToCache(country: string | null): void {
  if (typeof window === 'undefined' || !window.sessionStorage || !country) {
    return
  }

  sessionStorage.setItem(CACHE_KEY_COUNTRY, country)
}

/**
 * Определяет страну на сервере
 */
function detectCountryOnServer(): string | null {
  try {
    const headers = useRequestHeaders()
    return getCountryFromCdn(headers)
  } catch {
    return null
  }
}

/**
 * Определяет страну на клиенте с fallback на API
 */
async function detectCountryOnClient(useApiFallback: boolean): Promise<string | null> {
  // Проверяем CDN заголовки
  const cdnCountry = getCountryFromCdn()
  if (cdnCountry) {
    return cdnCountry
  }

  // Если CDN не найден и включен API fallback
  if (useApiFallback) {
    const apiCountry = await getCountryFromApi()
    if (apiCountry) {
      return apiCountry
    }
  }

  // Do NOT fall back to browser language — language ≠ country.
  // Ukrainian diaspora abroad have uk-UA browser locale but are physically outside Ukraine.
  // Returning 'UA' here would show Ukrainian region dropdown to international users.
  return null
}

/**
 * Composable для определения страны клиента по CDN заголовкам
 * Использует заголовки CDN/прокси на сервере, fallback на язык браузера или API
 */
export function useCountryDetection() {
  const countryCode = ref<string | null>(null)
  const isDetected = ref(false)

  /**
   * Определяет страну клиента
   * @param useApiFallback - Использовать API запрос как fallback, если CDN заголовки недоступны
   */
  const detectCountry = async (useApiFallback: boolean = false): Promise<string | null> => {
    if (isDetected.value) {
      return countryCode.value
    }

    let detectedCountry: string | null

    if (import.meta.server) {
      detectedCountry = detectCountryOnServer()
    } else {
      // /api/geo/country reads CF-IPCountry server-side — always fresh, no caching needed.
      // SessionStorage cache removed: it caused stale country for VPN users.
      detectedCountry = await detectCountryOnClient(useApiFallback)
    }

    countryCode.value = detectedCountry
    isDetected.value = true

    return detectedCountry
  }

  /**
   * Сбрасывает кеш определения страны
   */
  const resetDetection = () => {
    countryCode.value = null
    isDetected.value = false

    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(CACHE_KEY_COUNTRY)
    }
  }

  return {
    countryCode: readonly(countryCode),
    detectCountry,
    resetDetection,
    isDetected: readonly(isDetected),
  }
}
